import React, { useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WeeklyRoleCoverage from './WeeklyRoleCoverage';
import PositionOperationRulesSection from './PositionOperationRulesSection';
import PositionPayRatesSection from './PositionPayRatesSection';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDrawerHeaderFlushSx,
  harmonateDrawerBodySx,
  harmonateDrawerAccordionGroupSx,
  harmonateDrawerAccordionSx,
  harmonateDrawerAccordionSummarySx,
  harmonateDrawerAccordionTitleSx,
  harmonateDrawerAccordionDetailsSx,
  harmonateDrawerAddIconSx,
  harmonateDrawerEditIconSx,
  harmonateDrawerDeleteIconSx,
  harmonateDialogCloseIconSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const PositionDetailsDrawer = ({
  open,
  onClose,
  positionRow,
  selectedBusinessUnit,
  selectedProperty,
  token,
  baseUrl,
  tenantId,
  propertyId,
  showError,
  showWarning,
  showSuccess,
}) => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.WORKFORCE_OPTIMIZATION);
  const scopeBusinessUnit = selectedBusinessUnit || selectedProperty;
  const [expanded, setExpanded] = useState('coverage');
  const [selectedCoverage, setSelectedCoverage] = useState(null);
  const [selectedPayRate, setSelectedPayRate] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);
  const coverageRef = useRef(null);
  const payRatesRef = useRef(null);
  const rulesRef = useRef(null);
  const positionName = positionRow?.role_name || 'Position';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 760 },
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: ht.drawerBg,
        },
      }}
    >
      <Box sx={harmonateDrawerHeaderFlushSx}>
        <Typography variant="h6" sx={{ flex: 1, fontWeight: 700, color: ht.white }}>
          {positionName}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close drawer" sx={harmonateDialogCloseIconSx}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ ...harmonateDrawerBodySx, overflowY: 'auto' }}>
        <Box sx={harmonateDrawerAccordionGroupSx}>
        <Accordion
          expanded={expanded === 'coverage'}
          onChange={() => setExpanded(expanded === 'coverage' ? false : 'coverage')}
          disableGutters
          elevation={0}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>Weekly Position Coverage</Typography>
            <Tooltip title={selectedCoverage ? 'Delete Weekly Coverage' : 'Select a coverage window to delete'}>
              <span>
                <IconButton
                  size="small"
                  sx={harmonateDrawerDeleteIconSx}
                  disabled={!selectedCoverage || !canDelete}
                  onClick={(e) => {
                    e.stopPropagation();
                    coverageRef.current?.openDelete();
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={canAdd ? 'Add Weekly Coverage' : 'No add permission'}>
              <span>
              <IconButton
                size="small"
                sx={harmonateDrawerAddIconSx}
                disabled={!canAdd}
                onClick={(e) => {
                  e.stopPropagation();
                  coverageRef.current?.openAdd();
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
              </span>
            </Tooltip>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              ...harmonateDrawerAccordionDetailsSx,
              height: 340,
              overflow: 'hidden',
            }}
          >
            <WeeklyRoleCoverage
              ref={coverageRef}
              selectedBusinessUnit={scopeBusinessUnit}
              selectedRolePosition={positionRow}
              embedded
              accordionEmbedded
              hideToolbar
              onSelectionChange={setSelectedCoverage}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expanded === 'payRates'}
          onChange={() => setExpanded(expanded === 'payRates' ? false : 'payRates')}
          disableGutters
          elevation={0}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>Pay Rates</Typography>
            <Tooltip title="Edit Pay Rate">
              <span>
                <IconButton
                  size="small"
                  sx={harmonateDrawerEditIconSx}
                  disabled={!selectedPayRate || !canEdit}
                  onClick={(e) => {
                    e.stopPropagation();
                    payRatesRef.current?.openEdit();
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={canAdd ? 'Add Pay Rate' : 'No add permission'}>
              <span>
              <IconButton
                size="small"
                sx={harmonateDrawerAddIconSx}
                disabled={!canAdd}
                onClick={(e) => {
                  e.stopPropagation();
                  payRatesRef.current?.openAdd();
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
              </span>
            </Tooltip>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              ...harmonateDrawerAccordionDetailsSx,
              height: 400,
              overflow: 'hidden',
            }}
          >
            <PositionPayRatesSection
              ref={payRatesRef}
              positionRow={positionRow}
              token={token}
              baseUrl={baseUrl}
              tenantId={tenantId}
              propertyId={propertyId}
              showError={showError}
              showWarning={showWarning}
              showSuccess={showSuccess}
              active={open && expanded === 'payRates'}
              hideToolbar
              onSelectionChange={setSelectedPayRate}
            />
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expanded === 'rules'}
          onChange={() => setExpanded(expanded === 'rules' ? false : 'rules')}
          disableGutters
          elevation={0}
          sx={harmonateDrawerAccordionSx}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
            <Typography sx={harmonateDrawerAccordionTitleSx}>Operation Rules</Typography>
            <Tooltip title="Edit Operation Rule">
              <span>
                <IconButton
                  size="small"
                  sx={harmonateDrawerEditIconSx}
                  disabled={!selectedRule || !canEdit}
                  onClick={(e) => {
                    e.stopPropagation();
                    rulesRef.current?.openEdit();
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete Operation Rule">
              <span>
                <IconButton
                  size="small"
                  sx={harmonateDrawerDeleteIconSx}
                  disabled={!selectedRule || !canDelete}
                  onClick={(e) => {
                    e.stopPropagation();
                    rulesRef.current?.openDelete();
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={canAdd ? 'Add Operation Rule' : 'No add permission'}>
              <span>
              <IconButton
                size="small"
                sx={harmonateDrawerAddIconSx}
                disabled={!canAdd}
                onClick={(e) => {
                  e.stopPropagation();
                  rulesRef.current?.openAdd();
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
              </span>
            </Tooltip>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              ...harmonateDrawerAccordionDetailsSx,
              height: 400,
              overflow: 'hidden',
            }}
          >
            <PositionOperationRulesSection
              ref={rulesRef}
              positionRow={positionRow}
              token={token}
              baseUrl={baseUrl}
              tenantId={tenantId}
              propertyId={propertyId}
              showError={showError}
              showSuccess={showSuccess}
              active={open}
              hideToolbar
              onSelectionChange={setSelectedRule}
            />
          </AccordionDetails>
        </Accordion>
        </Box>
      </Box>
    </Drawer>
  );
};

export default PositionDetailsDrawer;
